// src/store/slices/authSlice.ts
import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { auth } from "../../types";
import { LOCAL_STORAGE } from "src/constants/common";
import { setUserAttributes } from "src/analytics";

// Initial state for the auth slice
const initialState: auth.UsersStateType = {
  clientSource: "default",
  mode: "",
  user: {
    data: {} as auth.UserType,
    isLoading: false,
    errors: "",
    platform: "ios",
  },
  isUserLoggedIn: false,
};

// Define the auth slice
const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    authenticateUserRequest: (
      state: auth.UsersStateType,
      action: PayloadAction<any>
    ) => {
      state.user.isLoading = true;
      state.user.errors = "";
    },
    authenticateUserSuccess: (
      state: auth.UsersStateType,
      action: PayloadAction<any>
    ) => {
      state.user.isLoading = false;
      state.user.data = action.payload.data;
      setUserAttributes(action.payload.data);
      localStorage.setItem(
        LOCAL_STORAGE.AUTH_TOKEN,
        action.payload.data?.token
      );
      localStorage.setItem(
        LOCAL_STORAGE.USER_INFO,
        JSON.stringify(action.payload.data)
      );
      state.isUserLoggedIn = true;
    },
    authenticateUserError: (
      state: auth.UsersStateType,
      { payload: error }: PayloadAction<string>
    ) => {
      state.user.isLoading = false;
      state.user.errors = error;
    },
    setClientSource: (
      state: auth.UsersStateType,
      action: PayloadAction<any>
    ) => {
      let clientSource =
        action.payload ||
        localStorage.getItem(LOCAL_STORAGE.CLIENT_SOURCE) ||
        "default";
      state.clientSource = clientSource;
    },
    setMode: (state: auth.UsersStateType, action: PayloadAction<any>) => {
      let mode =
        action.payload || localStorage.getItem(LOCAL_STORAGE.MODE) || "";
      state.mode = mode;
    },

    //update patient details
    updatePatientDetailsRequest: (
      state: auth.UsersStateType,
      action: PayloadAction<any>
    ) => {
      state.user.isLoading = true;
      state.user.errors = "";
    },
    updatePatientDetailsSuccess: (
      state: auth.UsersStateType,
      action: PayloadAction<any>
    ) => {
      state.user.isLoading = false;
    },
    updatePatientDetailsError: (
      state: auth.UsersStateType,
      { payload: error }: PayloadAction<string>
    ) => {
      state.user.isLoading = false;
      state.user.errors = error;
    },
    setUserData: (state: auth.UsersStateType, action: PayloadAction<any>) => {
      state.user.data = { ...state.user.data, ...action.payload };
      localStorage.setItem(
        LOCAL_STORAGE.USER_INFO,
        JSON.stringify(state.user.data)
      );
    },
    setPlatform: (
      state: auth.UsersStateType,
      action: PayloadAction<"ios" | "android">
    ) => {
      state.user.platform = action.payload;
    },
  },
});

// Export actions and reducer
export const {
  authenticateUserRequest,
  authenticateUserSuccess,
  authenticateUserError,
  setClientSource,
  setMode,
  updatePatientDetailsRequest,
  updatePatientDetailsSuccess,
  updatePatientDetailsError,
  setUserData,
  setPlatform,
} = authSlice.actions;

export default authSlice.reducer;
